import React, { useEffect, useMemo, useState } from 'react';
import type { CallStatistic } from 'types/call.type';
import {
    buildCallStatusSummaryCards,
    formatCount,
    formatStatusPercent,
    getExecutedCallsTotal,
    STATUS_ICON_THEME,
} from './callStatusSummary.utils';
import type { CallAnalyticsMixFilter } from '../DeepDive/callAnalyticsMix.types';
import { buildDispositionBreakdown } from '../DeepDive/callAnalyticsMix.utils';
import {
    CallDirectionChips,
    CallStatusCard,
    ProgressBreakdown,
    StatusDeepDivePanel,
    type ProgressLegendItem,
} from './components';

import styles from './CallStatusSummary.module.scss';

export interface CallStatusSummaryProps {
    callStats?: CallStatistic;
    humanTransfer?: boolean;
    activeFilter?: CallAnalyticsMixFilter | null;
    onFilterChange?: (filter: CallAnalyticsMixFilter | null) => void;
}

export const CallStatusSummary: React.FC<CallStatusSummaryProps> = ({
    callStats,
    humanTransfer = true,
    activeFilter = null,
    onFilterChange,
}) => {
    const cards = useMemo(() => buildCallStatusSummaryCards(callStats, humanTransfer), [callStats, humanTransfer]);
    const dispositionRows = useMemo(() => buildDispositionBreakdown(callStats), [callStats]);
    const [selectedCardId, setSelectedCardId] = useState<string | null>('queued');

    useEffect(() => {
        if (selectedCardId && !cards.some((card) => card.id === selectedCardId)) {
            setSelectedCardId(null);
        }
    }, [cards, selectedCardId]);

    const filteredTotal = getExecutedCallsTotal(callStats);
    const selectedCard = cards.find((card) => card.id === selectedCardId) ?? null;
    const selectedTheme = selectedCard ? STATUS_ICON_THEME[selectedCard.variant] : null;
    const dispositionTotal = useMemo(() => dispositionRows.reduce((sum, row) => sum + row.count, 0), [dispositionRows]);
    const dispositionItems: ProgressLegendItem[] = useMemo(
        () =>
            dispositionRows.map((row) => ({
                id: row.key,
                label: row.label,
                count: row.count,
                percent: formatStatusPercent(row.count, dispositionTotal),
                color: row.barColor,
            })),
        [dispositionRows, dispositionTotal]
    );
    const activeDispositionKey = activeFilter?.type === 'disposition' ? activeFilter.key : null;

    const handleCardClick = (cardId: string) => {
        setSelectedCardId((prev) => {
            if (prev === cardId) return prev;
            return cardId;
        });
    };

    const handleDispositionToggle = (item: ProgressLegendItem) => {
        if (!onFilterChange) return;

        const isSame = activeFilter?.type === 'disposition' && activeFilter.key === item.id;
        onFilterChange(isSame ? null : { type: 'disposition', key: item.id, label: item.label });
    };

    return (
        <section
            className={`${styles.section} flex flex-col gap-lg`}
            data-testid='call-status-summary'
            aria-label='Call status summary'
        >
            <div className='flex gap-x-2xl gap-y-sm items-baseline flex-wrap'>
                <h3>
                    Total calls executed <span className='text-5xl'>{formatCount(filteredTotal)}</span>
                </h3>
                <CallDirectionChips callStats={callStats} filteredTotal={filteredTotal} />
            </div>

            <div className={styles.grid} role='tablist' aria-label='Call status categories'>
                {cards.map((card) => (
                    <CallStatusCard
                        key={card.id}
                        card={card}
                        isSelected={selectedCardId === card.id}
                        onClick={handleCardClick}
                        showPercent={card.id !== 'queued' && card.id !== 'inProgress'}
                    />
                ))}
            </div>

            <div className={styles.detailRow}>
                {selectedCard && selectedTheme && (
                    <StatusDeepDivePanel card={selectedCard} statusColor={selectedTheme.color} />
                )}

                <ProgressBreakdown
                    title='Disposition breakdown'
                    subtitle='Outcome tags applied to filtered calls'
                    items={dispositionItems}
                    testId='call-status-disposition-breakdown'
                    activeItemId={activeDispositionKey}
                    isDimmed={activeFilter?.type === 'status'}
                    onItemToggle={handleDispositionToggle}
                />
            </div>
        </section>
    );
};
